import { changed, noOp, rejected, type Command, type CommandExecution } from './commands';
import { damage } from './damage';
import { createId, findLayer, type EditorDocument, type Layer, type LayerId } from './model';
import { applyTonePixels, type ToneSettings } from '../filters/tone';
import { applyHighPassPixels } from '../filters/high-pass';
import { applyDisplacePixels, type DisplaceSettings, type GrayscaleDisplacementMap } from '../filters/displace';
import { isSmartObject } from './smart-objects';

export const SMART_FILTERS_EXTENSION = 'maipo:smart-filters';

export interface ToneSmartFilter {
  id: string;
  name: string;
  type: 'tone';
  enabled: boolean;
  settings: ToneSettings;
}

export interface HighPassSmartFilter {
  id: string;
  name: string;
  type: 'high-pass';
  enabled: boolean;
  radius: number;
}

export interface DisplaceSmartFilter {
  id: string;
  name: string;
  type: 'displace';
  enabled: boolean;
  map: GrayscaleDisplacementMap;
  settings: DisplaceSettings;
}

export type SmartFilter = ToneSmartFilter | HighPassSmartFilter | DisplaceSmartFilter;

function finite(value: unknown, fallback: number, minimum: number, maximum: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.max(minimum, Math.min(maximum, value)) : fallback;
}

function normalizeTone(value: unknown): ToneSettings {
  const source = value && typeof value === 'object' ? value as Partial<ToneSettings> : {};
  return {
    brightness: finite(source.brightness, 0, -1, 1),
    contrast: finite(source.contrast, 0, -0.99, 0.99),
    saturation: finite(source.saturation, 0, -1, 1),
    invert: Boolean(source.invert),
  };
}

function validFilter(value: unknown): value is SmartFilter {
  if (!value || typeof value !== 'object') return false;
  const source = value as Partial<SmartFilter>;
  if (typeof source.id !== 'string' || typeof source.name !== 'string') return false;
  if (source.type === 'high-pass') return true;
  if (source.type === 'tone') return 'settings' in source && source.settings !== null && typeof source.settings === 'object';
  if (source.type !== 'displace' || !('map' in source) || !source.map || typeof source.map !== 'object') return false;
  const map = source.map as Partial<GrayscaleDisplacementMap>;
  return typeof map.width === 'number' && typeof map.height === 'number' && Array.isArray(map.values)
    && map.width >= 1 && map.height >= 1 && map.width <= 512 && map.height <= 512 && map.values.length === Math.round(map.width) * Math.round(map.height);
}

export function smartFilters(layer: Layer | undefined): SmartFilter[] {
  if (!isSmartObject(layer)) return [];
  const source = layer.extensions[SMART_FILTERS_EXTENSION];
  if (!Array.isArray(source)) return [];
  return source.filter(validFilter).slice(0, 32).map((filter): SmartFilter => filter.type === 'high-pass' ? {
    id: filter.id,
    name: filter.name.slice(0, 100),
    type: 'high-pass',
    enabled: filter.enabled !== false,
    radius: finite(filter.radius, 4, 1, 64),
  } : filter.type === 'displace' ? {
    id: filter.id,
    name: filter.name.slice(0, 100),
    type: 'displace',
    enabled: filter.enabled !== false,
    map: {
      width: Math.round(filter.map.width),
      height: Math.round(filter.map.height),
      values: filter.map.values.map((value) => Math.round(finite(value, 128, 0, 255))),
    },
    settings: {
      horizontalScale: finite(filter.settings?.horizontalScale, 10, -1_000, 1_000),
      verticalScale: finite(filter.settings?.verticalScale, 10, -1_000, 1_000),
      mode: filter.settings?.mode === 'tile' ? 'tile' : 'fit',
    },
  } : {
    id: filter.id,
    name: filter.name.slice(0, 100),
    type: 'tone',
    enabled: filter.enabled !== false,
    settings: normalizeTone(filter.settings),
  });
}

export function createToneSmartFilter(name = 'Tone'): ToneSmartFilter {
  return {
    id: createId('smart-filter'),
    name: name.trim().slice(0, 100) || 'Tone',
    type: 'tone',
    enabled: true,
    settings: { brightness: 0, contrast: 0, saturation: 0, invert: false },
  };
}

export function createHighPassSmartFilter(radius = 4): HighPassSmartFilter {
  return {
    id: createId('smart-filter'),
    name: 'High Pass',
    type: 'high-pass',
    enabled: true,
    radius: finite(radius, 4, 1, 64),
  };
}

export function createDisplaceSmartFilter(map: GrayscaleDisplacementMap, settings: DisplaceSettings): DisplaceSmartFilter {
  if (!Number.isInteger(map.width) || !Number.isInteger(map.height) || map.width < 1 || map.height < 1 || map.width > 512 || map.height > 512 || map.values.length !== map.width * map.height) {
    throw new Error('Persistent displacement maps must be between 1×1 and 512×512 pixels.');
  }
  return {
    id: createId('smart-filter'),
    name: 'Displace',
    type: 'displace',
    enabled: true,
    map: structuredClone(map),
    settings: {
      horizontalScale: finite(settings.horizontalScale, 10, -1_000, 1_000),
      verticalScale: finite(settings.verticalScale, 10, -1_000, 1_000),
      mode: settings.mode === 'tile' ? 'tile' : 'fit',
    },
  };
}

export function applySmartFiltersToPixels(pixels: Uint8ClampedArray, width: number, height: number, filters: readonly SmartFilter[]): void {
  for (const filter of filters) if (filter.enabled) {
    if (filter.type === 'tone') applyTonePixels(pixels, filter.settings);
    else if (filter.type === 'high-pass') applyHighPassPixels(pixels, width, height, filter.radius);
    else pixels.set(applyDisplacePixels(pixels, width, height, filter.map, filter.settings));
  }
}

export class SetSmartFiltersCommand implements Command {
  readonly label: string;
  private before?: SmartFilter[];

  constructor(private readonly layerId: LayerId, private readonly filters: readonly SmartFilter[], label = 'Update Smart Filters') {
    this.label = label;
  }

  retainedBytes(): number {
    return 256 + JSON.stringify(this.filters).length * 2 + JSON.stringify(this.before ?? []).length * 2;
  }

  execute(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (!isSmartObject(layer)) return rejected('Smart Filters require an embedded Smart Object.');
    this.before ??= smartFilters(layer);
    if (JSON.stringify(this.before) === JSON.stringify(this.filters)) return noOp();
    if (this.filters.length) layer.extensions[SMART_FILTERS_EXTENSION] = structuredClone(this.filters.slice(0, 32));
    else delete layer.extensions[SMART_FILTERS_EXTENSION];
    return changed(this.damage());
  }

  undo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (!isSmartObject(layer)) return;
    if (this.before?.length) layer.extensions[SMART_FILTERS_EXTENSION] = structuredClone(this.before);
    else delete layer.extensions[SMART_FILTERS_EXTENSION];
  }

  damage() { return damage({ layerIds: [this.layerId], fullDocument: true }); }
}
